import { useRef, useMemo, useEffect, useCallback } from 'react';
import type { MinimalDocument } from '../../../types';
import type { SelectionSource } from '../../../types/documentSelection';
import logger from '../../../utils/simpleLogger';

/**
 * Hook para sincronizar documentos entre props y context
 *
 * Responsabilidades:
 * - Mantener refs de documentos procesados
 * - Sincronizar selección de documentos con context
 * - Cargar metadatos de documentos
 * - Gestionar auto-selección desde selector de contexto
 *
 * @param params - Parámetros de sincronización
 * @returns Funciones y estado de sincronización
 */

interface UseDocumentSyncProps {
  selectedMinimalDocuments: MinimalDocument[];
  contextSelectedIds: string[];
  selectContextDocuments: (documentIds: string[], source?: SelectionSource) => void;
  deselectContextDocuments: (documentIds: string[]) => void;
  clearContextSelection: () => void;
  loadDocument: (documentId: string) => Promise<void>;
  setDocument: (document: MinimalDocument) => void;
  onDocumentSelect?: (documentIds: string[]) => void;
  inputMessage: string;
  setInputMessage: (message: string | ((prev: string) => string)) => void;
}

export function useDocumentSync({
  selectedMinimalDocuments,
  contextSelectedIds,
  selectContextDocuments,
  deselectContextDocuments,
  clearContextSelection,
  loadDocument,
  setDocument,
  onDocumentSelect,
  inputMessage,
  setInputMessage
}: UseDocumentSyncProps) {
  // Create stable list of document IDs to avoid infinite loops
  const selectedDocIds = useMemo(() =>
    selectedMinimalDocuments.map(d => d.id).sort().join(','),
    [selectedMinimalDocuments]
  );

  // Store documents in ref to avoid dependency on selectedMinimalDocuments array
  const selectedDocsRef = useRef(selectedMinimalDocuments);
  selectedDocsRef.current = selectedMinimalDocuments;

  // Only set documents in context when IDs change (for metadata loading)
  const lastProcessedIdsRef = useRef<string>('');

  useEffect(() => {
    // Only process if IDs have actually changed
    if (selectedDocIds === lastProcessedIdsRef.current) {
      return;
    }

    lastProcessedIdsRef.current = selectedDocIds;

    // Set all documents in context using ref to avoid dependency on array
    selectedDocsRef.current.forEach(doc => {
      setDocument(doc);
    });
  }, [selectedDocIds, setDocument]);

  // Track which document IDs we've already added mentions for to avoid duplicates
  const processedDocumentIdsRef = useRef<Set<string>>(new Set());

  // Handler para auto-selección de documentos desde el selector de contexto
  const handleDocumentsAutoSelect = useCallback((documentIds: string[]) => {
    logger.info('🔄 [useDocumentSync] handleDocumentsAutoSelect called', {
      documentIds,
      count: documentIds.length,
      previousCount: contextSelectedIds.length
    });

    // ENABLED: Documents selected from context selector should be added to context
    // This allows them to be displayed as chips and used in chat queries
    if (documentIds.length > 0) {
      // Update context with new selection (this replaces the previous selection)
      selectContextDocuments(documentIds, 'mention-filters');

      // Find documents that were deselected (in previous selection but not in new)
      const deselectedIds = contextSelectedIds.filter(id => !documentIds.includes(id));

      // Remove mentions for deselected documents from input
      if (deselectedIds.length > 0) {
        // Get document names for deselected documents to remove their mentions
        const deselectedDocs = selectedMinimalDocuments.filter(doc => deselectedIds.includes(doc.id));
        deselectedDocs.forEach(doc => {
          const docName = (doc.name || doc.original_filename || '').replace(/@/g, '').trim();
          if (docName) {
            // Remove the mention from input (delimitador | es obligatorio)
            setInputMessage(prev => {
              const escapedDocName = docName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const mentionPattern = new RegExp(`@documento:${escapedDocName}\\|`, 'g');
              return prev.replace(mentionPattern, '').replace(/\s+/g, ' ').trim();
            });
          }
        });

        // Also deselect from context
        deselectContextDocuments(deselectedIds);
      }

      // Load document data asynchronously for newly selected documents
      const newlySelectedIds = documentIds.filter(id => !contextSelectedIds.includes(id));
      newlySelectedIds.forEach(docId => {
        loadDocument(docId).catch(error => {
          logger.info('⚠️ [useDocumentSync] Failed to load document for chip', {
            documentId: docId,
            error
          });
        });
      });

      // Also notify parent component if callback is available
      if (onDocumentSelect) {
        onDocumentSelect(documentIds);
      }
      logger.info('✅ [useDocumentSync] Documents updated in context and notified parent', {
        documentIds,
        count: documentIds.length,
        deselectedCount: deselectedIds.length,
        newlySelectedCount: newlySelectedIds.length
      });
    } else {
      // If empty, clear selection and remove document mentions from input
      clearContextSelection();

      // Remove all @documento: mentions from input (delimitador | es obligatorio)
      const cleanedText = inputMessage.replace(/@documento:[^|@]+\|/g, '').replace(/\s+/g, ' ').trim();
      setInputMessage(cleanedText);

      if (onDocumentSelect) {
        onDocumentSelect([]);
      }
    }
  }, [
    selectContextDocuments,
    onDocumentSelect,
    clearContextSelection,
    loadDocument,
    inputMessage,
    contextSelectedIds,
    selectedMinimalDocuments,
    deselectContextDocuments,
    setInputMessage
  ]);

  return {
    selectedDocIds,
    processedDocumentIdsRef,
    handleDocumentsAutoSelect
  };
}
